import React, { useCallback, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import { AddCollaborators, updateCollaboratorRole, removeCollaborator } from '../../store/actions/marketplace2/caseActions';
import { fetchAllEmployees } from '../../store/actions/organization/employeeActions';
import { PiTrashLight } from 'react-icons/pi';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { useSelector } from 'react-redux';
import { fetchExternalCollaborators } from '../../store/actions/organization/LawfirmActions';

interface CollaboratorsPanelProps {
  caseId: string;
  summary: any;
  onClose: () => void;
  onUpdate: () => void;
}

const ROLES = [
  { value: 'Attorney', label: 'Attorney' },
  { value: 'Litigator', label: 'Litigator' },
  { value: 'Paralegal', label: 'Paralegal' },
  { value: 'Arbitrator', label: 'Arbitrator' },
  { value: 'Mediator', label: 'Mediator' },
  { value: 'Conciliator', label: 'Conciliator' },
  { value: 'Fact-Finder', label: 'Fact-Finder' },
  { value: 'Ombudsman', label: 'Ombudsman' },
];

const CollaboratorsPanel = forwardRef((props: CollaboratorsPanelProps, ref) => {
  const { caseId, summary, onUpdate } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const{ employees, externalCollaborators} = useSelector((state: RootState) => state.employees);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [collaborators, setCollaborators] = useState<any[]>([]);

  const fetchPeople = useCallback(async () => {
    dispatch(fetchAllEmployees());
    dispatch(fetchExternalCollaborators());
    if (summary?.Collaborators) {
      setCollaborators(summary.Collaborators.map((collab: any) => ({
        ...collab,
        UserID: collab.UserID,
        Role: collab.Role || '', 
      })));
    }
  },[dispatch, summary])

  useEffect(() => {
    fetchPeople()
  }, [fetchPeople]);

  const internal_people = employees
  .filter(emp => !collaborators.some(collab => collab.UserID === emp.UserID))
  .map(emp => ({
    value: emp.UserID,
    label: `${emp.FirstName} ${emp.LastName}`,
  }));

  const external_people = externalCollaborators
  .filter(emp => !collaborators.some(collab => collab.UserID === emp.UserID))
  .map(emp => ({
    value: emp.UserID,
    label: `${emp.FirstName} ${emp.LastName} (External)`,
  }));

  const employeeOptions =  [...internal_people, ...external_people]

  const handleAddCollaborators = async () => {
    if (selectedEmployeeIds.length === 0) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Please select at least one person'),
        modalType: 'error',
        duration: 3000,
      }));
      return;
    }
    if (selectedEmployeeIds.length !== selectedRoles.length) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Please assign a role for each selected employee'),
        modalType: 'error',
        duration: 3000,
      }));
      return;
    }

    try {
      await dispatch(AddCollaborators(caseId, selectedEmployeeIds, selectedRoles));
      setSelectedEmployeeIds([]);
      setSelectedRoles([]);
      onUpdate();
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Access granted successfully'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (error) {
      if(!error) return 
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to grant access'),
        modalType: 'error',
        duration: 3000,
      }));
    }
  };

  const handleRemoveCollaborator = async (collaboratorId: string) => {
    dispatch(showModal({
      type: 'confirmation',
      message: t('Remove Access'),
      subMsg: t('This person will lose access. This action cannot be undone.'),
      isDest: true,
      icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
      onConfirm: async () => {
        try {
          await dispatch(removeCollaborator(caseId, collaboratorId));
          setCollaborators(prev => prev.filter(c => c.UserID !== collaboratorId));
          onUpdate();
          dispatch(hideModal())
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Access removed successfully'),
            modalType: 'success',
            duration: 3000,
          }));
        } catch (error) {
          if(!error) return
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Failed to remove access'),
            modalType: 'error',
            duration: 3000,
          }));
        }
      },
      showModal: true,
    }));
  };

  const handleRoleChange = async (collaboratorId: string, newRole: string) => {
    if (!newRole) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Please select a valid role'),
        modalType: 'error',
        duration: 3000,
      }));
      return;
    }
    try {
      await dispatch(updateCollaboratorRole(caseId, collaboratorId, newRole));
      setCollaborators(prev =>
        prev.map(collab =>
          collab.UserID === collaboratorId ? { ...collab, Role: newRole } : collab
        )
      );
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Role updated successfully'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (error) {
      if(!error) return
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to update role'),
        modalType: 'error',
        duration: 3000,
      }));
    }
  };

  useImperativeHandle(ref, () => ({
    handleAddCollaborators,
  }));

  return (
    <div className="px-6 space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t('Current Access')} ({collaborators.length})</h3>
        {collaborators.length > 0 ? (
          <ul className="space-y-4">
            {collaborators.map((collab) => (
              <li key={collab.UserID} className="flex items-center justify-between p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center gap-4">
                  <span className="font-medium">{collab.FullName}</span>
                  <span className="text-sm text-gray-500">({collab.Email})</span>
                </div>
                <div className="flex items-center gap-4">
                  <Dropdown
                    options={[{ value: '', label: t('No Role') }, ...ROLES]}
                    value={collab.Role || ''}
                    onChange={(val) => handleRoleChange(collab.UserID, val as string)}
                    placeholder={t('Select role')}
                  />
                  <button
                    onClick={() => handleRemoveCollaborator(collab.UserID)}
                    className="text-gray-500 hover:text-red-700"
                  >
                    <PiTrashLight className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">{t('No one has access yet')}</p>
        )}
      </div>
      <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium">{t('Grant Access')}</h3>
        <Dropdown
          label={t('Select Person')}
          placeholder={t('Choose a person to grant access')}
          options={employeeOptions}
          value={selectedEmployeeIds[0] || ''}
          onChange={(val) => setSelectedEmployeeIds([val as string])}
        />
        <Dropdown
          label={t('Assign Role')}
          placeholder={t('Select role')}
          options={ROLES}
          value={selectedRoles[0] || ''}
          onChange={(val) => setSelectedRoles([val as string])}
        />
      </div>
    </div>
  );
});

export default CollaboratorsPanel;